// src/pages/Slough.jsx
import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import "../styles/slough.css";

const Slough = () => {
  return (
    <div className="slough-info-page">
      <Helmet>
        <title>
          Slough Transport & Taxi Guide – Stations, Heathrow Transfers & Local Cabs | CheapCabGrab
        </title>
        <meta
          name="description"
          content="Plan your journey from Slough. Guide to Slough Station, Wexham Park Hospital, Trading Estate, Langley, Burnham & Iver taxis, plus Heathrow transfer options with CheapCabGrab."
        />
        <link rel="canonical" href="https://www.cheapcabgrab.com/slough" />
        <meta
          name="keywords"
          content="Slough taxi, Slough transport, Slough to Heathrow, Slough station taxi, Wexham Park Hospital taxi, Slough Trading Estate taxi, SL1 SL2 SL3 cabs, CheapCabGrab"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Slough Transport & Taxi Guide – Stations, Heathrow Transfers & Local Cabs"
        />
        <meta
          property="og:description"
          content="Travel guide for Slough: stations, hospital & business pickups, Heathrow transfers and local taxi options with CheapCabGrab."
        />
        <meta property="og:url" content="https://www.cheapcabgrab.com/slough" />

        {/* JSON-LD – Informational taxi service guide for Slough */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: "CheapCabGrab – Slough Transport & Taxi Guide",
            url: "https://www.cheapcabgrab.com/slough",
            areaServed: ["Slough SL1", "Slough SL2", "Slough SL3", "Langley", "Burnham", "Iver"],
            provider: {
              "@type": "TaxiService",
              name: "CheapCabGrab",
              url: "https://www.cheapcabgrab.com",
            },
            hasPart: [
              {
                "@type": "WebPage",
                name: "Slough Taxi Booking",
                url: "https://www.cheapcabgrab.com/slough-taxi",
              },
              {
                "@type": "WebPage",
                name: "Heathrow Taxi",
                url: "https://www.cheapcabgrab.com/heathrow-taxi",
              },
              {
                "@type": "WebPage",
                name: "Fleet",
                url: "https://www.cheapcabgrab.com/fleet",
              },
            ],
          })}
        </script>
      </Helmet>

      {/* TOP STRIP */}
      <div className="slough-info-top-strip">
        <div className="slough-info-top-strip-inner">
          <span className="slough-info-strip-label">Slough Travel Guide</span>
          <span className="slough-info-strip-item">
            🚆 Slough Station: fast trains to London & connections to Heathrow by taxi.
          </span>
          <span className="slough-info-strip-item">
            🚖 CheapCabGrab covers SL1–SL3, Slough Trading Estate, Wexham Park Hospital & nearby
            stations.
          </span>
          <span className="slough-info-strip-item">
            🛫 Fixed-price taxis to Heathrow, Gatwick, Luton & Stansted from key Slough locations.
          </span>
        </div>
      </div>

      <main className="slough-info-main">
        {/* HERO / INTRO */}
        <section
          className="slough-info-hero"
          aria-labelledby="slough-info-hero-heading"
        >
          <div className="slough-info-hero-text">
            <h1 id="slough-info-hero-heading">
              Slough Transport & Taxi Guide (SL1–SL3)
            </h1>
            <p>
              This page gives you a quick, practical guide to travelling from Slough – including
              station pickups, hospital transfers, business journeys and airport connections.
            </p>
            <p>
              When you’re ready to book, use our dedicated{" "}
              <Link to="/slough-taxi">Slough taxi booking page</Link> for fixed-price quotes and
              online payment.
            </p>
            <ul className="slough-info-hero-list">
              <li>✅ Taxis from Slough Station, town centre and Wexham Park Hospital.</li>
              <li>✅ Business travel from Slough Trading Estate & nearby offices.</li>
              <li>✅ SL1, SL2 & SL3 coverage including Langley, Burnham & Iver.</li>
              <li>✅ Fixed-price airport transfers with CheapCabGrab – no surge pricing.</li>
            </ul>
          </div>

          <aside className="slough-info-hero-side">
            <div className="slough-info-hero-card">
              <h2>Need a Slough taxi now?</h2>
              <p>
                Ready to book? Go straight to{" "}
                <Link to="/slough-taxi">Slough Taxi – CheapCabGrab</Link> for live quotes.
              </p>
              <ul>
                <li>🚖 Fixed-price taxis – Slough ↔ Heathrow & London.</li>
                <li>🧳 Extra luggage & family trips with MPV/minibus options.</li>
                <li>📧 Email confirmation & secure online payment.</li>
              </ul>
              <Link to="/slough-taxi" className="slough-info-primary-btn">
                Book a Slough Taxi →
              </Link>
            </div>

            <div className="slough-info-hero-links">
              <h3>Quick links</h3>
              <ul>
                <li>
                  ✈️ <Link to="/heathrow-taxi">Heathrow Taxi</Link>
                </li>
                <li>
                  🚐 <Link to="/minibus">Minibus Hire</Link> /{" "}
                  <Link to="/coach">Coach Hire</Link>
                </li>
                <li>
                  🚗 <Link to="/fleet">Our Fleet</Link>
                </li>
                <li>
                  👨‍✈️ <Link to="/our-drivers">Our Drivers</Link>
                </li>
                <li>
                  ❓ <Link to="/faq">FAQ</Link>
                </li>
              </ul>
            </div>
          </aside>
        </section>

        {/* SLOUGH AREAS */}
        <section className="slough-info-section" id="slough-areas">
          <header className="slough-info-section-header">
            <h2>🏙️ Slough at a Glance – SL1, SL2 & SL3</h2>
            <p>
              CheapCabGrab covers the whole of Slough – from the town centre and estates to
              nearby villages and stations.
            </p>
          </header>
          <div className="slough-info-grid">
            <article className="slough-info-card">
              <h3>SL1 – Central Slough</h3>
              <ul>
                <li>🏢 Town centre shops and main bus routes</li>
                <li>🚆 Close to Slough Station and main taxi ranks</li>
                <li>🚖 Common starting point for Heathrow & London journeys</li>
              </ul>
            </article>
            <article className="slough-info-card">
              <h3>SL2 – Wexham & North Slough</h3>
              <ul>
                <li>🏥 Includes Wexham Park Hospital</li>
                <li>🏡 Residential areas with regular hospital & airport transfers</li>
                <li>🚗 Easy access to A355 & M40 corridors</li>
              </ul>
            </article>
            <article className="slough-info-card">
              <h3>SL3 – Langley, Colnbrook & Iver Area</h3>
              <ul>
                <li>🚆 Langley & Iver Stations for London links</li>
                <li>🏭 Colnbrook / Poyle business & logistics areas</li>
                <li>🛫 Close to Heathrow & airport hotels</li>
              </ul>
            </article>
          </div>
        </section>

        {/* KEY PICKUP POINTS */}
        <section className="slough-info-section" id="slough-pickups">
          <header className="slough-info-section-header">
            <h2>📍 Key Pickup Points in Slough</h2>
            <p>
              These are the most common pickup spots our passengers choose in and around
              Slough.
            </p>
          </header>

          <div className="slough-info-grid slough-info-grid-2">
            <article className="slough-info-card">
              <h3>Slough Station (Main Taxi Hub)</h3>
              <ul>
                <li>🚆 Fast trains to London & Reading</li>
                <li>🚖 Ideal for rail + taxi connections to Heathrow</li>
                <li>
                  💡 When booking online, select{" "}
                  <strong>“Slough Station, Slough”</strong> as your pickup.
                </li>
              </ul>
            </article>

            <article className="slough-info-card">
              <h3>Slough Town Centre</h3>
              <ul>
                <li>🏢 Queensmere Observatory & High Street area</li>
                <li>🍽️ Shops, restaurants and local services</li>
                <li>🚖 Taxis to Heathrow, Windsor, Maidenhead & London</li>
              </ul>
            </article>

            <article className="slough-info-card">
              <h3>Wexham Park Hospital</h3>
              <ul>
                <li>🏥 Hospital appointments, staff and visitor journeys</li>
                <li>🚗 Transfers across Slough, Langley & surrounding areas</li>
                <li>🛫 Direct taxis to Heathrow for overseas patients & visitors</li>
              </ul>
            </article>

            <article className="slough-info-card">
              <h3>Slough Trading Estate & Business Parks</h3>
              <ul>
                <li>🏭 Offices, data centres & logistics hubs</li>
                <li>🚖 Regular business trips to Heathrow & London</li>
                <li>🚐 Minibus/coach options for teams and corporate events</li>
              </ul>
            </article>

            <article className="slough-info-card">
              <h3>Nearby Stations – Langley, Burnham & Iver</h3>
              <ul>
                <li>🚆 Connections via Elizabeth Line & GWR</li>
                <li>🚗 Short taxi hops to homes, hotels & business locations</li>
                <li>🛫 Combined train + taxi routes to Heathrow & London</li>
              </ul>
            </article>
          </div>
        </section>

        {/* AIRPORT OPTIONS */}
        <section className="slough-info-section" id="slough-airports">
          <header className="slough-info-section-header">
            <h2>🛫 Slough to Heathrow & Other Airports</h2>
            <p>
              Slough is one of the closest major towns to Heathrow. Here are typical ways
              passengers travel between Slough and the main airports.
            </p>
          </header>

          <div className="slough-info-airport-grid">
            <article className="slough-info-card slough-info-airport-card">
              <h3>Slough ↔ Heathrow</h3>
              <ul>
                <li>🚖 <strong>Taxi:</strong> typically 15–25 minutes depending on traffic.</li>
                <li>
                  ✅ Best for: luggage, family travel, late-night/early-morning flights,
                  business trips.
                </li>
                <li>
                  🔗 Book via{" "}
                  <Link to="/heathrow-taxi">Heathrow Taxi – CheapCabGrab</Link>.
                </li>
              </ul>
            </article>

            <article className="slough-info-card slough-info-airport-card">
              <h3>Slough ↔ Gatwick, Luton & Stansted</h3>
              <ul>
                <li>🛣️ Longer journeys along M25 / M1 / M11 corridors</li>
                <li>🚖 Taxis & minibuses available for groups and families</li>
                <li>
                  🔗 See{" "}
                  <Link to="/slough-taxi">Slough Taxi page</Link> for guide fares and
                  booking.
                </li>
              </ul>
            </article>

            <article className="slough-info-card slough-info-airport-card">
              <h3>Train & Other Options</h3>
              <ul>
                <li>🚆 Slough Station → London Paddington / Elizabeth Line</li>
                <li>🔁 Many passengers combine train + taxi for flexibility</li>
                <li>
                  ❓ Check our <Link to="/faq">FAQ</Link> for tips on planning your journey.
                </li>
              </ul>
            </article>
          </div>
        </section>

        {/* BUSINESS & GROUP TRAVEL */}
        <section className="slough-info-section" id="slough-business">
          <header className="slough-info-section-header">
            <h2>🏢 Business, Events & Group Travel from Slough</h2>
            <p>
              Many companies around Slough Trading Estate, Langley and Colnbrook use
              CheapCabGrab for staff travel, airport runs and events.
            </p>
          </header>

          <div className="slough-info-grid slough-info-grid-2">
            <article className="slough-info-card">
              <h3>Business Travel</h3>
              <ul>
                <li>🚖 Regular taxis to Heathrow & central London</li>
                <li>🕒 Early-morning and late-evening pickups supported</li>
                <li>
                  👨‍✈️ See{" "}
                  <Link to="/our-drivers">Our Drivers</Link> for licensing & training info.
                </li>
              </ul>
            </article>
            <article className="slough-info-card">
              <h3>Group Travel & Events</h3>
              <ul>
                <li>🚐 Minibus & coach options for teams and conferences</li>
                <li>🏟️ Transfers to Wembley, O2, Twickenham and event venues</li>
                <li>
                  🔗 Learn more on our <Link to="/services">Services</Link>,{" "}
                  <Link to="/minibus">Minibus</Link> and{" "}
                  <Link to="/coach">Coach</Link> pages.
                </li>
              </ul>
            </article>
          </div>
        </section>

        {/* SUPPORT / CONTACT */}
        <section className="slough-info-section" id="slough-help">
          <header className="slough-info-section-header">
            <h2>📞 Need Help Planning Your Journey from Slough?</h2>
          </header>
          <p>
            If you are unsure which option is best – taxi, minibus, coach or a mix of train
            and taxi – our team can help you plan the most practical route.
          </p>
          <p>
            Contact us via our <Link to="/contact">support page</Link>, or email
            <strong> support@cheapcabgrab.com</strong>.
          </p>
          <p>
            For terminal and airline details, try our{" "}
            <Link to="/terminalFinder">Terminal Finder</Link> and{" "}
            <Link to="/airlineContacts">Airline Contacts</Link> tools.
          </p>
        </section>
      </main>

      {/* FLOATING CTA – DESKTOP */}
      <button
        className="slough-info-floating-cta"
        onClick={() => {
          window.location.href = "/slough-taxi";
        }}
      >
        🚖 Book Slough Taxi
      </button>

      {/* BOTTOM CTA – MOBILE */}
      <div className="slough-info-bottom-cta">
        <button
          className="slough-info-bottom-cta-btn"
          onClick={() => {
            window.location.href = "/slough-taxi";
          }}
        >
          🚖 Book a Slough Taxi with CheapCabGrab
        </button>
      </div>
    </div>
  );
};

export default Slough;

